import { Component, signal, computed, input, OnInit, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { MemberModel } from '../../../../core/models/data/member.model';
import { RoleModel } from '../../../../core/models/data/role.model';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { MemberService } from '../../../../core/services/member.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { MemberListSortOptions } from '../../../../core/enums/sort-options.enums';
import { MemberListPage } from '../../../member/components/member-list/member-list.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes, MemberListViewLocations } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MemberStatuses } from '../../../../core/enums/data.enums';
import { DisableWithoutPermissionDirective } from "../../../../core/directives/disable-without-permission/disable-without-permission.directive";
import { RolePermission } from '../../../../core/enums/permissions.enums';
import { AvatarComponent } from "../../../../parts/avatar/avatar.component";
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DismissibleNotesService } from '../../../../core/services/dismissible-notes.service';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

@Component({
  selector: 'ms-manage-organization-admins',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, MemberListPage, ConfirmationModalComponent, InfoModalComponent, DisableWithoutPermissionDirective, AvatarComponent, TooltipDirective],
  templateUrl: './manage-organization-admins.component.html'
})
export class ManageOrganizationAdminsComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private memberService = inject(MemberService);
  private userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);
  public dismissibleNotesService = inject(DismissibleNotesService);

  public elevatedPrivilegesWarning = OrganizationsMessagingConstants.MANAGE_ADMINS_ELEVATED_PRIVILEGES_WARNING();
  public elevatedPrivilegesWarningDismissKey = OrganizationsMessagingConstants.MANAGE_ADMINS_ELEVATED_PRIVILEGES_WARNING_DISMISS_KEY();

  public organization = input.required<OrganizationModel>();

  public isLoadingRoles = signal<boolean>(false);
  public isLoadingAdmins = signal<boolean>(false);
  public currentAdmins = signal<MemberModel[]>([]);
  public errorMessage = signal<string | undefined>(undefined);
  public orgAdminRoleId = signal<number | undefined>(undefined);
  public orgMemberRoleId = signal<number | undefined>(undefined);
  public selectedMemberForAdmin = signal<MemberModel | undefined>(undefined);
  public selectedAdminForRemoval = signal<MemberModel | undefined>(undefined);
  public isSubmitting = signal<boolean>(false);

  public isLoading = computed(() => this.isLoadingRoles() || this.isLoadingAdmins());
  public currentAdminIds = computed(() => this.currentAdmins().map(admin => admin.memberId));
  public MemberListViewLocations = MemberListViewLocations;

  public RolePermission = RolePermission;

  // Avatar
  public avatarSize = AvatarConstants.SIZE().MEMBER_CARD;
  public avatarShape = AvatarConstants.SHAPE().MEMBER_CARD;
  public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().MEMBER_CARD;

  @ViewChild('grantAdminConfirmationModalRef') grantAdminConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('revokeAdminConfirmationModalRef') revokeAdminConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('adminInfoModalRef') adminInfoModalRef!: InfoModalComponent;
  @ViewChild(MemberListPage) memberListComponent?: MemberListPage;

  async ngOnInit(): Promise<void> {
    await this.loadAdminRole();
    if (this.orgAdminRoleId()) {
      await this.loadCurrentAdmins();
    }
  }

  private async loadAdminRole(): Promise<void> {
    this.isLoadingRoles.set(true);
    this.errorMessage.set(undefined);
    
    try {
      const rolesResult = await this.authorizationService.getRoles(false);

      if (!rolesResult.success || !rolesResult.data) {
        this.errorMessage.set(rolesResult.message || 'Failed to load roles.');
        return;
      }

      //TODO: DEV-1315 - Create enums for RoleNames
      const adminRole = rolesResult.data.find(
        (role: RoleModel) => role.name.toLowerCase() === 'organization administrator'
      );

      if (!adminRole) {
        this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_ADMIN_NOT_FOUND());
        return;
      }

      this.orgAdminRoleId.set(adminRole.roleId);
      
      //TODO: DEV-1315 - Create enums for RoleNames
      const memberRole = rolesResult.data.find(
        (role: RoleModel) => role.name.toLowerCase() === 'organization member'
      );
      
      if (memberRole) {
        this.orgMemberRoleId.set(memberRole.roleId);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_LOADING_ROLES());
    } finally {
      this.isLoadingRoles.set(false);
    }
  }

  private async loadCurrentAdmins(): Promise<void> {
    this.isLoadingAdmins.set(true);
    this.errorMessage.set(undefined);
    
    try {
      if (!this.orgAdminRoleId()) {
        this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_ADMIN_ROLEID_NOT_FOUND());
        return;
      }

      const memberFilter: MembersFilter = {
        organizationIds: [this.organization().organizationId],
        roleIds: [this.orgAdminRoleId()!],
        // Admins can hold the role while Created/Invited/Expired (assigned before first login), so
        // the admin list must include those statuses, not just Active.
        statuses: [MemberStatuses.Created, MemberStatuses.Invited, MemberStatuses.Expired, MemberStatuses.Active]
      };

      const membersResult = await this.memberService.searchMembers(
        memberFilter, 
        MemberListSortOptions.NameASC, 
        0, 
        100
      );

      if (!membersResult.success || !membersResult.data) {
        this.errorMessage.set(membersResult.message || OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_LOADING_ADMINS());
        return;
      }

      this.currentAdmins.set(membersResult.data.data || []);
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_LOADING_ADMINS(), error);
      this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_LOADING_ADMINS());
    } finally {
      this.isLoadingAdmins.set(false);
    }
  }

  public openMemberSelector(): void {
    this.memberListComponent?.loadMembers(false);
    this.bootstrapService.manageModal('#manageAdminsMemberSelectorModal', ModalActions.Show);
  }

  public onMemberSelectedForAdmin(member: MemberModel): void {
    this.selectedMemberForAdmin.set(member);
    this.bootstrapService.manageModal('#manageAdminsMemberSelectorModal', ModalActions.Hide);
    
    this.grantAdminConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.grantAdminConfirmationModalRef.enableNotes.set(true);

    const confirmationMessageData = {firstName: member.firstName, lastName: member.lastName};
    this.grantAdminConfirmationModalRef.message.set(
      OrganizationsMessagingConstants.ORGANIZATIONS_GRANT_ADMIN_PERMISSION().replaceValuesWithObject(confirmationMessageData)
    );

    this.grantAdminConfirmationModalRef.enableTypeToConfirm.set(false);
    this.grantAdminConfirmationModalRef.confirmButtonText.set('Grant Admin Role');
    this.grantAdminConfirmationModalRef.confirmButtonClassName.set('btn-primary');
    
    this.bootstrapService.manageModal('#grantAdminConfirmationModal', ModalActions.Show);
  }

  public async onGrantAdminConfirmed(): Promise<void> {
    const memberToGrant = this.selectedMemberForAdmin();
    if (!memberToGrant || !this.orgAdminRoleId()) return;

    this.isSubmitting.set(true);

    try {
      const result = await this.memberService.grantRoleToMember(
        memberToGrant.memberId, 
        this.orgAdminRoleId()!
      );

      this.adminInfoModalRef.message.set(result.message);
      if (result.success) {
        const activeUser = this.userSessionService.activeUser();
        
        if (activeUser && activeUser.userId === memberToGrant.userId) {
          await this.authorizationStateService.refreshContext();
        }

        await this.loadCurrentAdmins();

        this.adminInfoModalRef.modalType.set(ModalTypes.Success);
      } else {
        this.adminInfoModalRef.modalType.set(ModalTypes.Failure);
      }
    } catch (error) {
      console.error('Error granting admin role:', error);
      this.adminInfoModalRef.modalType.set(ModalTypes.Failure);
      this.adminInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_GRANTING_ADMIN_PERMISSION());
    } finally {
      this.isSubmitting.set(false);
      this.bootstrapService.manageModal('#grantAdminConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#adminInfoModal', ModalActions.Show);
    }
  }

  public onGrantAdminCancelled(): void {
    this.bootstrapService.manageModal('#grantAdminConfirmationModal', ModalActions.Hide);
    this.selectedMemberForAdmin.set(undefined);
    this.bootstrapService.manageModal('#manageAdminsMemberSelectorModal', ModalActions.Show);
  }

  public confirmRevokeAdmin(admin: MemberModel): void {
    this.selectedAdminForRemoval.set(admin);
    
    this.revokeAdminConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.revokeAdminConfirmationModalRef.enableNotes.set(true);

    const confirmationMessageData = {firstName: admin.firstName, lastName: admin.lastName};
    const confirmationMessage = OrganizationsMessagingConstants.ORGANIZATIONS_REMOVE_ADMIN_ROLE().replaceValuesWithObject(confirmationMessageData);

    this.revokeAdminConfirmationModalRef.message.set(confirmationMessage);
    this.revokeAdminConfirmationModalRef.enableTypeToConfirm.set(false);
    this.revokeAdminConfirmationModalRef.confirmButtonText.set('Unassign Admin Role');
    this.revokeAdminConfirmationModalRef.confirmButtonClassName.set('btn-danger');
    
    this.bootstrapService.manageModal('#revokeAdminConfirmationModal', ModalActions.Show);
  }

  public async onRevokeAdminConfirmed(): Promise<void> {
    const adminToRevoke = this.selectedAdminForRemoval();
    if (!adminToRevoke || !this.orgAdminRoleId()) return;

    this.isSubmitting.set(true);

    try {
      const result = await this.memberService.revokeRoleFromMember(
        adminToRevoke.memberId, 
        this.orgAdminRoleId()!
      );

      this.adminInfoModalRef.message.set(result.message);
      if (result.success) {
        const activeUser = this.userSessionService.activeUser();
        
        if (activeUser && activeUser.userId === adminToRevoke.userId) {
          await this.authorizationStateService.refreshContext();
        }

        await this.loadCurrentAdmins();

        this.adminInfoModalRef.modalType.set(ModalTypes.Success);
      } else {
        this.adminInfoModalRef.modalType.set(ModalTypes.Failure);
      }
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_REMOVING_ADMIN_ROLE(), error);
      this.adminInfoModalRef.modalType.set(ModalTypes.Failure);
      this.adminInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_REMOVING_ADMIN_ROLE());
    } finally {
      this.isSubmitting.set(false);
      this.bootstrapService.manageModal('#revokeAdminConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#adminInfoModal', ModalActions.Show);
    }
  }

  public onRevokeAdminCancelled(): void {
    this.bootstrapService.manageModal('#revokeAdminConfirmationModal', ModalActions.Hide);
    this.selectedAdminForRemoval.set(undefined);
  }
}
